import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  IndianRupee, ArrowDownCircle, ArrowUpCircle, ArrowUpRight,
  Clock, Plus, Filter, X, RefreshCw, Trophy,
  ChevronLeft, ChevronRight, ExternalLink, FileText, Download
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

const TYPE_META = {
  partner_payout:            { label: 'Booking Payout',        color: '#16a34a', bg: '#DCEFB8', dir: 'in' },
  tournament_earning:        { label: '🏆 Tournament Earning',  color: '#8b5cf6', bg: '#f3e8ff', dir: 'in' },
  cancellation_compensation: { label: 'Cancel Compensation',   color: '#f59e0b', bg: '#fef3c7', dir: 'in' },
  credit:                    { label: 'Top-up',                 color: '#2563eb', bg: '#eff6ff', dir: 'in' },
  debit:                     { label: 'Debit',                  color: '#dc2626', bg: '#fee2e2', dir: 'out' },
  withdrawal:                { label: 'Withdrawal',             color: '#dc2626', bg: '#fee2e2', dir: 'out' },
  partner_fee_debit:         { label: 'Platform Fee',           color: '#9333ea', bg: '#f3e8ff', dir: 'out' },
  partner_payout_reversal:   { label: 'Payout Reversal',       color: '#dc2626', bg: '#fee2e2', dir: 'out' },
};

export default function PartnerWallet({ data: propData }) {
  const { token } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [filter, setFilter] = useState('All Transactions');
  const [showAddMoney, setShowAddMoney] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankDetails, setBankDetails] = useState({ 
    accountHolderName: '', 
    upiId: '', 
    accountNumber: '', 
    ifscCode: '', 
    bankName: '',
    accountType: 'savings'
  });
  const [processing, setProcessing] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);

  const downloadInvoice = async (txnId, invoiceNo) => {
    setDownloadingInvoiceId(txnId);
    try {
      const res = await axios.get(`${API}/wallet/payouts/${txnId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TurfX_Payout_Invoice_${invoiceNo || txnId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Invoice download error:', err);
      alert('Failed to download invoice PDF.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const fetchBankDetails = async () => {
    try {
      const res = await axios.get(`${API}/wallet/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.hasBankDetails && res.data?.bankDetails) {
        setBankDetails({
          accountHolderName: res.data.bankDetails.accountHolderName || '',
          upiId: res.data.bankDetails.upiId || '',
          accountNumber: res.data.bankDetails.accountNumber || '',
          ifscCode: res.data.bankDetails.ifscCode || '',
          bankName: res.data.bankDetails.bankName || '',
          accountType: res.data.bankDetails.accountType || 'savings'
        });
      }
    } catch (err) {
      console.error('Error fetching bank details:', err);
    }
  };

  useEffect(() => {
    if (showWithdraw) {
      fetchBankDetails();
    }
  }, [showWithdraw]);

  useEffect(() => {
    const isTest = process.env.REACT_APP_PAYU_IS_TEST === 'true';
    const scriptSrc = isTest 
      ? 'https://jssdk-uat.payu.in/bolt/bolt.min.js' 
      : 'https://jssdk.payu.in/bolt/bolt.min.js';

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const fetchWallet = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/partner/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWalletData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWallet(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const balance = walletData?.balance || 0;
  const transactions = walletData?.transactions || [];
  const settlements = walletData?.settlements || [];

  const filtered = transactions.filter(t => {
    if (filter === 'Credits') return ['partner_payout','cancellation_compensation','credit'].includes(t.type);
    if (filter === 'Debits')  return ['withdrawal','debit','partner_fee_debit','partner_payout_reversal'].includes(t.type);
    return true;
  });

  const totalCredits  = transactions.filter(t => TYPE_META[t.type]?.dir === 'in').reduce((s, t) => s + t.amount, 0);
  const totalDebits   = transactions.filter(t => TYPE_META[t.type]?.dir === 'out').reduce((s, t) => s + t.amount, 0);
  const lastTxn       = transactions[0];

  const handleAddMoney = async () => {
    const amount = parseFloat(addAmount);
    if (!amount || amount < 100) return alert('Minimum amount is ₹100');
    setProcessing(true);
    try {
      const orderRes = await axios.post(`${API}/wallet/add/create-order`,
        { amount }, { headers: { Authorization: `Bearer ${token}` } },
      );
      
      const { key, orderId, amount: orderAmt, firstname, email, phone, productinfo, hash, udf1, udf2, udf3, udf4, udf5, demo } = orderRes.data;

      if (demo) {
        await axios.post(`${API}/wallet/add/verify`, {
          amount,
          payu_txnid: orderId,
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        alert('Money added successfully!');
        setShowAddMoney(false);
        setAddAmount('');
        fetchWallet();
        return;
      }

      // Redirect to PayU payment page
      const payuUrl = process.env.REACT_APP_PAYU_IS_TEST === 'true'
        ? 'https://test.payu.in/_payment'
        : 'https://secure.payu.in/_payment';

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payuUrl;

      const cbUrl = `${API}/wallet/add/payu-callback`;
      const secureCbUrl = cbUrl.startsWith('http://') && !API.includes('localhost') && !API.includes('127.0.0.1')
        ? 'https://' + cbUrl.slice(7)
        : cbUrl;

      const params = {
        key,
        txnid: orderId,
        amount: parseFloat(orderAmt).toFixed(2),
        productinfo,
        firstname,
        email,
        phone: phone ? phone.replace(/\D/g, '') : '',
        surl: secureCbUrl,
        furl: secureCbUrl,
        hash,
        udf1,
        udf2,
        udf3,
        udf4,
        udf5,
        service_provider: 'payu_paisa'
      };

      Object.entries(params).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = v || '';
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      return;
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < 1) return alert('Minimum withdrawal is ₹1');
    if (amount > balance) return alert('Insufficient balance');
    if (!bankDetails.accountHolderName.trim()) {
      return alert('Account holder name is required');
    }
    if (!bankDetails.upiId.trim()) {
      return alert('UPI ID is required (e.g. name@bank)');
    }
    if (bankDetails.accountNumber && bankDetails.accountNumber.trim()) {
      const cleanAcc = bankDetails.accountNumber.trim();
      if (!/^\d{9,18}$/.test(cleanAcc)) {
        return alert('Bank account number must be between 9 and 18 digits.');
      }
    }
    if (bankDetails.ifscCode && bankDetails.ifscCode.trim()) {
      const cleanIfsc = bankDetails.ifscCode.trim();
      if (!/^[A-Za-z0-9]{6,14}$/.test(cleanIfsc)) {
        return alert('IFSC code must be 6 to 14 alphanumeric characters.');
      }
    }
    setProcessing(true);
    try {
      // 1. Save bank details first
      await axios.post(`${API}/wallet/bank-details`, {
        accountHolderName: bankDetails.accountHolderName.trim(),
        upiId: bankDetails.upiId.trim(),
        accountNumber: bankDetails.accountNumber?.trim() || null,
        ifscCode: bankDetails.ifscCode?.trim() || null,
        bankName: bankDetails.bankName?.trim() || null,
        accountType: bankDetails.accountType || 'savings'
      }, { headers: { Authorization: `Bearer ${token}` } });

      // 2. Submit withdrawal
      await axios.post(`${API}/wallet/withdraw`, {
        amount,
        bankAccount: {
          accountHolderName: bankDetails.accountHolderName.trim(),
          upiId: bankDetails.upiId.trim(),
          accountNumber: bankDetails.accountNumber?.trim() || null,
          ifscCode: bankDetails.ifscCode?.trim() || null,
          bankName: bankDetails.bankName?.trim() || null
        }
      }, { headers: { Authorization: `Bearer ${token}` } });
      alert('Withdrawal request submitted successfully! You will receive your requested amount to your account through UPI within 2 business days.');
      setShowWithdraw(false);
      setWithdrawAmount('');
      fetchWallet();
    } catch (err) {
      alert(err.response?.data?.msg || err.response?.data?.detail || 'Failed to submit withdrawal request');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '700' }}>Loading wallet...</div>;

  const tournamentEarnings = transactions
    .filter(t => (t.type === 'tournament_earning' || t.type === 'tournament_payout') && t.status === 'success')
    .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  const stats = [
    { label: 'Available Balance', value: `₹${balance.toLocaleString()}`, icon: <IndianRupee size={22} />, color: '#CEF17B', sub: 'Turf & Tournament net balance' },
    { label: 'Turf Booking Earnings', value: `₹${totalCredits.toLocaleString()}`, icon: <ArrowDownCircle size={22} />, color: '#3b82f6', sub: 'Turf booking payouts' },
    { label: '🏆 Tournament Earnings', value: `₹${tournamentEarnings.toLocaleString()}`, icon: <Trophy size={22} />, color: '#8b5cf6', sub: 'Tournament entry payouts' },
    { label: 'Total Withdrawn', value: `₹${totalDebits.toLocaleString()}`, icon: <ArrowUpCircle size={22} />, color: '#f59e0b', sub: 'Withdrawals & fees' },
  ];

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#161616', marginBottom: '8px' }}>Wallet</h2>
          <p style={{ color: '#374151', fontWeight: '500' }}>
            Auto-payouts from Admin Wallet • Next payout in ~5 minutes after booking
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={fetchWallet} style={btnSecondary}><RefreshCw size={16} /> Refresh</button>
          <button onClick={() => setShowAddMoney(true)} style={btnSecondary}><Plus size={18} /> Add Money</button>
          <button onClick={() => setShowWithdraw(true)} style={btnPrimary}><ArrowUpRight size={18} /> Withdraw</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.2rem', marginBottom: '2.5rem' }}>
        {stats.map((s, i) => (
          <div key={i} style={statCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${s.color}15`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#374151', marginBottom: '3px' }}>{s.label}</div>
                <div style={{ fontSize: '1.3rem', fontWeight: '900', color: '#161616' }}>{s.value}</div>
              </div>
            </div>
            <div style={{ fontSize: '0.72rem', fontWeight: '600', color: '#374151' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <div className="table-responsive" style={{ background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6', overflowX: 'auto' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1.5px solid #EEF2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {['All Transactions', 'Credits', 'Debits'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '6px 14px', borderRadius: '8px', fontSize: '0.8rem',
                  fontWeight: '700', cursor: 'pointer', border: '1.5px solid',
                  borderColor: filter === f ? '#084734' : '#EEF2E6',
                  background: filter === f ? '#DCEFB8' : 'white',
                  color: filter === f ? '#084734' : '#374151',
                }}
              >{f}</button>
            ))}
          </div>
          <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '600' }}>{filtered.length} records</span>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#F8FAF7', borderBottom: '1.5px solid #EEF2E6' }}>
            <tr>
              {['Date', 'Description', 'Type', 'Amount', 'Status'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="5" style={{ padding: '3rem', textAlign: 'center', color: '#374151', fontWeight: '700' }}>No wallet transactions yet</td></tr>
            ) : filtered.map((t, i) => {
              const meta = TYPE_META[t.type] || { label: t.type, color: '#374151', bg: '#F8FAF7', dir: 'in' };
              return (
                <tr 
                  key={t.id} 
                  onClick={() => setSelectedTxn(t)} 
                  style={{ 
                    borderBottom: i < filtered.length - 1 ? '1px solid #EEF2E6' : 'none',
                    cursor: 'pointer' 
                  }}
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '700', fontSize: '0.85rem' }}>
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#374151' }}>
                      {t.createdAt ? new Date(t.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: meta.bg, color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {meta.dir === 'in' ? <ArrowDownCircle size={15} /> : <ArrowUpCircle size={15} />}
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#161616' }}>{t.description}</div>
                        {t.bookingRef && <div style={{ fontSize: '0.72rem', color: '#374151' }}>Booking #{t.bookingRef}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ background: meta.bg, color: meta.color, padding: '3px 10px', borderRadius: '999px', fontSize: '0.7rem', fontWeight: '800' }}>
                      {meta.label}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '900', fontSize: '0.95rem', color: meta.dir === 'in' ? '#084734' : '#dc2626' }}>
                      {meta.dir === 'in' ? '+' : '-'}₹{Math.abs(t.amount).toLocaleString()}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      background: t.status === 'success' ? '#DCEFB8' : '#FEF3C7',
                      color: t.status === 'success' ? '#065F46' : '#92400E',
                      padding: '3px 10px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: '800',
                    }}>{(t.status || 'success').toUpperCase()}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Money Modal */}
      {showAddMoney && (
        <div style={modalOverlay} onClick={() => setShowAddMoney(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h3 style={{ fontWeight: '900', fontSize: '1.1rem' }}>Add Money to Wallet</h3>
              <X size={20} onClick={() => setShowAddMoney(false)} style={{ cursor: 'pointer' }} />
            </div>
            <label style={lblStyle}>Amount (₹)</label>
            <input type="number" value={addAmount} onChange={e => setAddAmount(e.target.value)} placeholder="Min ₹100" style={inpStyle} />
            <div style={{ display: 'flex', gap: '10px', marginTop: '1.25rem' }}>
              <button onClick={handleAddMoney} disabled={processing} style={{ ...btnPrimary, flex: 1, justifyContent: 'center' }}>
                {processing ? 'Processing...' : 'Pay via PayU'}
              </button>
              <button onClick={() => setShowAddMoney(false)} style={{ ...btnSecondary, flex: 1, justifyContent: 'center' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdraw && (
        <div style={modalOverlay} onClick={() => setShowWithdraw(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <h3 style={{ fontWeight: '900', fontSize: '1.1rem' }}>Withdraw Money</h3>
              <X size={20} onClick={() => setShowWithdraw(false)} style={{ cursor: 'pointer' }} />
            </div>
            {(() => {
              const pendingTotal = transactions.filter(t => t.type === 'withdrawal' && t.status === 'pending').reduce((s, t) => s + (t.amount || 0), 0);
              const avail = Math.max(0, balance - pendingTotal);
              return (
                <div style={{ background: '#DCEFB8', borderRadius: '10px', padding: '12px', marginBottom: '1rem', fontSize: '0.85rem', color: '#084734', fontWeight: '700' }}>
                  <div>Available for Withdrawal: <strong>₹{avail.toLocaleString()}</strong> (Total Wallet: ₹{balance.toLocaleString()})</div>
                  {pendingTotal > 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#854d0e', marginTop: '4px', fontWeight: '600' }}>
                      ⏳ ₹{pendingTotal.toLocaleString()} in active/pending withdrawal requests
                    </div>
                  )}
                </div>
              );
            })()}
            {[
              { label: 'Amount (₹)', key: 'amount', type: 'number', placeholder: 'Min ₹1' },
              { label: 'UPI ID (Required for payout)', key: 'upiId', placeholder: 'e.g. name@bank' },
              { label: 'Account Holder Name', key: 'accountHolderName', placeholder: 'As per bank records' },
              { label: 'Account Number (optional)', key: 'accountNumber', placeholder: 'Account number' },
              { label: 'IFSC Code (optional)', key: 'ifscCode', placeholder: 'IFSC code' },
              { label: 'Bank Name (optional)', key: 'bankName', placeholder: 'Bank name' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: '0.9rem' }}>
                <label style={lblStyle}>{f.label}</label>
                <input
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={f.key === 'amount' ? withdrawAmount : bankDetails[f.key] || ''}
                  onChange={e => f.key === 'amount' ? setWithdrawAmount(e.target.value) : setBankDetails({ ...bankDetails, [f.key]: e.target.value })}
                  style={inpStyle}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: '10px', marginTop: '1.25rem' }}>
              <button onClick={handleWithdraw} disabled={processing} style={{ ...btnPrimary, flex: 1, justifyContent: 'center' }}>
                {processing ? 'Processing...' : 'Submit Request'}
              </button>
              <button onClick={() => setShowWithdraw(false)} style={{ ...btnSecondary, flex: 1, justifyContent: 'center' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {selectedTxn && (
        <div style={modalOverlay} onClick={() => setSelectedTxn(null)}>
          <div style={{ ...modalBox, width: '520px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1.5px solid #EEF2E6', paddingBottom: '0.75rem' }}>
              <h3 style={{ fontWeight: '900', fontSize: '1.2rem', color: '#161616', margin: 0 }}>Transaction Details</h3>
              <X size={20} onClick={() => setSelectedTxn(null)} style={{ cursor: 'pointer', color: '#374151' }} />
            </div>
            
            <div style={{ textAlign: 'center', padding: '1.25rem', background: '#F8FAF7', borderRadius: '16px', marginBottom: '1.5rem', border: '1px solid #EEF2E6' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '800', textTransform: 'uppercase', color: '#374151', marginBottom: '4px', letterSpacing: '0.5px' }}>
                {TYPE_META[selectedTxn.type]?.label || selectedTxn.type}
              </div>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '900', 
                color: (TYPE_META[selectedTxn.type]?.dir === 'in') ? '#16a34a' : '#dc2626' 
              }}>
                {(TYPE_META[selectedTxn.type]?.dir === 'in' ? '+' : '-')}₹{Math.abs(selectedTxn.amount).toLocaleString()}
              </div>
              <div style={{ marginTop: '10px' }}>
                <span style={{
                  background: selectedTxn.status === 'success' ? '#D1FAE5' : '#FEF3C7',
                  color: selectedTxn.status === 'success' ? '#065F46' : '#92400E',
                  padding: '4px 12px', borderRadius: '999px', fontSize: '0.68rem', fontWeight: '800',
                }}>
                  {(selectedTxn.status || 'success').toUpperCase()}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              <div style={detailRow}>
                <span style={detailLabel}>Description</span>
                <span style={detailValue}>{selectedTxn.description}</span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Date & Time</span>
                <span style={detailValue}>
                  {selectedTxn.createdAt ? new Date(selectedTxn.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </span>
              </div>
              <div style={detailRow}>
                <span style={detailLabel}>Internal Transaction ID</span>
                <span style={{ ...detailValue, fontFamily: 'monospace', fontSize: '0.75rem' }}>{selectedTxn.id}</span>
              </div>
              
              {selectedTxn.bookingRef && (
                <div style={detailRow}>
                  <span style={detailLabel}>Booking Reference</span>
                  <span style={{ ...detailValue, fontWeight: '700', color: '#084734' }}>#{selectedTxn.bookingRef}</span>
                </div>
              )}

              {(selectedTxn.razorpayPaymentId || selectedTxn.paymentId || selectedTxn.payuMihpayid) && (
                <div style={detailRow}>
                  <span style={detailLabel}>{selectedTxn.payuMihpayid ? 'PayU ID' : 'Razorpay Payment ID'}</span>
                  <span style={{ ...detailValue, fontFamily: 'monospace', fontSize: '0.75rem', color: '#2563eb' }}>
                    {selectedTxn.payuMihpayid || selectedTxn.razorpayPaymentId || selectedTxn.paymentId}
                  </span>
                </div>
              )}

              {(selectedTxn.razorpayOrderId || selectedTxn.payuTxnId) && (
                <div style={detailRow}>
                  <span style={detailLabel}>{selectedTxn.payuTxnId ? 'PayU Txn ID' : 'Razorpay Order ID'}</span>
                  <span style={{ ...detailValue, fontFamily: 'monospace', fontSize: '0.75rem' }}>{selectedTxn.payuTxnId || selectedTxn.razorpayOrderId}</span>
                </div>
              )}

              {selectedTxn.metadata?.invoice_number && (
                <div style={detailRow}>
                  <span style={detailLabel}>Invoice Number</span>
                  <span style={{ ...detailValue, color: '#16a34a', fontWeight: '800', fontFamily: 'monospace' }}>
                    {selectedTxn.metadata.invoice_number}
                  </span>
                </div>
              )}

              {selectedTxn.metadata && Object.keys(selectedTxn.metadata).length > 0 && (
                <div style={{ marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid #EEF2E6' }}>
                  <div style={{ fontWeight: '800', fontSize: '0.8rem', color: '#161616', marginBottom: '0.5rem' }}>Transaction Metadata</div>
                  <div style={{ background: '#F8FAF7', borderRadius: '10px', padding: '10px', fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {Object.entries(selectedTxn.metadata).map(([key, val]) => (
                      <div key={key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#374151', textTransform: 'capitalize' }}>
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span style={{ fontWeight: '700', color: '#161616' }}>
                          {(typeof val === 'number' && (key.toLowerCase().includes('amount') || key.toLowerCase().includes('fee') || key.toLowerCase().includes('payout') || key.toLowerCase().includes('gst'))) ? `₹${val}` : String(val)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(selectedTxn.type === 'withdrawal' || selectedTxn.type === 'partner_payout') && selectedTxn.status === 'success' && (
                <button
                  onClick={() => downloadInvoice(selectedTxn.id, selectedTxn.metadata?.invoice_number)}
                  disabled={downloadingInvoiceId === selectedTxn.id}
                  style={{ ...btnPrimary, width: '100%', justifyContent: 'center', padding: '12px' }}
                >
                  <FileText size={16} /> {downloadingInvoiceId === selectedTxn.id ? 'Generating PDF...' : 'Download Settlement Invoice (PDF)'}
                </button>
              )}
              <button onClick={() => setSelectedTxn(null)} style={{ ...btnSecondary, flex: 1, justifyContent: 'center' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const statCard   = { background: 'white', padding: '1.5rem', borderRadius: '20px', border: '1.5px solid #EEF2E6' };
const btnPrimary = { background: '#CEF17B', color: '#084734', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const btnSecondary = { background: 'white', color: '#374151', border: '1.5px solid #EEF2E6', padding: '10px 20px', borderRadius: '10px', fontWeight: '700', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const thStyle    = { textAlign: 'left', padding: '1rem 1.25rem', fontSize: '0.72rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle    = { padding: '1rem 1.25rem', verticalAlign: 'middle' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
const modalBox   = { background: 'white', borderRadius: '20px', padding: '2rem', width: '460px', maxWidth: '95%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' };
const lblStyle   = { display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#374151', marginBottom: '6px' };
const inpStyle   = { width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #EEF2E6', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' };
const detailRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '6px 0',
  borderBottom: '1px solid #F3F4F6',
};
const detailLabel = {
  color: '#374151',
  fontWeight: '600',
};
const detailValue = {
  color: '#161616',
  fontWeight: '700',
  textAlign: 'right',
};
